import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Box, Button, TextField } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import ImageModal from "components/ImageModal";
import EmptyData from "components/EmptyData";
import tripMonitorAuthorTable from "layouts/tables/data/tripMonitorAuthorTable";
import ShowChatData from "Utils/showChatData";
import EscortReport from "Utils/escortReport";
import File from "Utils/file";
import NotificationModal from "Utils/NotificationModal";
import apiClient from "Utils/apiClient";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const normalizeCollection = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.values(value).filter(Boolean);
  }

  return [];
};

const sortByTimestampDesc = (items = []) =>
  [...items].sort((a, b) => {
    const getTimeVal = (x) => {
      if (!x) return 0;
      if (typeof x.currentTimeStamp === "number" && !Number.isNaN(x.currentTimeStamp) && x.currentTimeStamp > 0) {
        return x.currentTimeStamp;
      }
      const raw = x.createdAt || x.startTime || x.escortEndedAt || x.tripendtime || x.currentTimeStamp || 0;
      const parsed = new Date(raw).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return getTimeVal(b) - getTimeVal(a);
  });

const formatTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
};

function UserEscortHistory() {
  const { userId } = useParams();

  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState("short");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [openNotification, setOpenNotification] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState(null);

  const { columns } = tripMonitorAuthorTable();

  const handleImageClick = (url) => {
    if (!url) return;
    setIsModalOpen(true);
    setOnClickImageData(url);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleOpenNotification = (notifications) => {
    setSelectedNotifications(notifications);
    setOpenNotification(true);
  };

  const handleCloseNotification = () => {
    setOpenNotification(false);
    setSelectedNotifications(null);
  };

  const fetchHistoryData = async () => {
    if (!userId) {
      return null;
    }
    const response = await apiClient.get(`/users/${userId}/history`);
    const historyData = response?.data?.data || response?.data || response || null;
    console.log("Fetched history response:", response, "Parsed historyData:", historyData);
    return historyData;
  };

  const loadShortDistance = (historyData) => {
    const { user = {}, shortDistance } = historyData;
    const normalized = normalizeCollection(shortDistance);
    const trips = sortByTimestampDesc(normalized);

    return trips.map((shortTrip, index) => {
      const regNo =
        shortTrip?.vehicleReg ||
        shortTrip?.regNo ||
        shortTrip?.driverId?.vehicleReg ||
        shortTrip?.driverId?.vehiclePlate ||
        "N/A";

      const vehicleImg =
        shortTrip?.driverId?.vehicleImage ||
        shortTrip?.driverId?.driverImage ||
        shortTrip?.imgVehicle ||
        shortTrip?.vehicleImage ||
        user?.userImage;

      const dateDisplay =
        shortTrip?.ArrivalDate ||
        formatTime(shortTrip?.createdAt || shortTrip?.currentTimeStamp || shortTrip?.startTime);

      const timeDisplay =
        shortTrip?.TripTime ||
        shortTrip?.ArrivalTime ||
        formatTime(shortTrip?.startTime || shortTrip?.createdAt || shortTrip?.currentTimeStamp);

      const endDisplay = formatTime(shortTrip?.endTime || shortTrip?.tripendtime);

      return {
        Sr: index + 1,
        Name: user?.userName || "N/A",
        Email: user?.userEmail || "N/A",
        Mobile: user?.userPhone || "N/A",
        VehicleReg: regNo,
        State: shortTrip?.state || user?.state || "N/A",
        VehicleImage: (
          <img
            onClick={() => handleImageClick(vehicleImg)}
            src={vehicleImg}
            alt="vehicle"
            style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
          />
        ),
        TripDate: dateDisplay || "N/A",
        TripTime: timeDisplay || "N/A",
        TripEndTime: endDisplay || "N/A",
        IsMoving: shortTrip?.status || shortTrip?.Trip_Status || "Active",
        TripType: <h5>Short Distance</h5>,
        StartRide:
          shortTrip?.startingLocation ||
          shortTrip?.currentLocation ||
          shortTrip?.currentLocTxt ||
          shortTrip?.pickupLocation ||
          "N/A",
        Notification:
          shortTrip?.contactNotificationStatus && shortTrip?.contactNotificationStatus?.length > 0 ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleOpenNotification(shortTrip.contactNotificationStatus)}
            >
              Show Notification
            </button>
          ) : (
            "No Notification"
          ),
        File: <File props={shortTrip} />,
        Chat: <ShowChatData props={shortTrip} />,
        Report: <EscortReport props={{ ...user, ShortDistance_Escort: [shortTrip] }} />,
        Download: <span style={{ color: "#6b7280" }}>Not Saved</span>,
        EndRide:
          shortTrip?.destination || shortTrip?.finalDestination ? (
            <span style={{ fontWeight: 600 }}>{shortTrip?.destination || shortTrip?.finalDestination}</span>
          ) : ["COMPLETED", "ESCALATED", "CANCELED"].includes(shortTrip?.status) ? (
            <span style={{ color: "#b91c1c", fontWeight: 600 }}>{shortTrip?.status}</span>
          ) : (
            <span style={{ color: "#15803d", fontWeight: 600 }}>Active</span>
          ),
        currentTimeStamp: new Date(shortTrip?.createdAt || shortTrip?.currentTimeStamp || shortTrip?.startTime || 0).getTime(),
      };
    });
  };

  const loadLongDistance = (historyData) => {
    const { user = {}, longDistance } = historyData;
    const normalized = normalizeCollection(longDistance);
    const tours = sortByTimestampDesc(normalized);

    return tours.map((trip, index) => {
      const regNo =
        trip?.vehicleReg ||
        trip?.regNo ||
        trip?.driverId?.vehicleReg ||
        trip?.driverId?.vehiclePlate ||
        "N/A";

      const vehicleImg =
        trip?.driverId?.vehicleImage ||
        trip?.driverId?.driverImage ||
        trip?.imgVehicle ||
        trip?.vehicleImage ||
        user?.userImage;

      const dateDisplay =
        trip?.selectedDate ||
        trip?.ArrivalDate ||
        formatTime(trip?.createdAt || trip?.currentTimeStamp);

      const timeDisplay =
        trip?.selectedTime ||
        trip?.TripTime ||
        trip?.ArrivalTime ||
        formatTime(trip?.startTime || trip?.createdAt || trip?.currentTimeStamp);

      const endDisplay = formatTime(trip?.endTime || trip?.tripendtime);

      return {
        Sr: index + 1,
        Name: user?.userName || "N/A",
        Email: user?.userEmail || "N/A",
        Mobile: user?.userPhone || "N/A",
        VehicleReg: regNo,
        State: trip?.state || user?.state || "N/A",
        VehicleImage: (
          <img
            onClick={() => handleImageClick(vehicleImg)}
            src={vehicleImg}
            alt="vehicle"
            style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
          />
        ),
        TripDate: dateDisplay || "N/A",
        TripTime: timeDisplay || "N/A",
        TripEndTime: endDisplay || "N/A",
        IsMoving: (
          <Button
            variant="contained"
            sx={{
              backgroundColor:
                trip?.status === "ACTIVE" || trip?.Trip_Status === "Moving"
                  ? "green"
                  : trip?.status === "COMPLETED" || trip?.Trip_Status === "Completed"
                    ? "red"
                    : "yellow",
              color: "white",
            }}
          >
            {trip?.status || trip?.Trip_Status || "Unknown"}
          </Button>
        ),
        TripType: <h5>Long Distance</h5>,
        StartRide:
          trip?.startingLocation ||
          trip?.curruntLocation ||
          trip?.loadingPark ||
          trip?.currentLocation ||
          trip?.pickupLocation ||
          "N/A",
        Notification: "N/A",
        File: <File props={trip} />,
        Chat: <ShowChatData props={trip} />,
        Report: <EscortReport props={{ ...user, ShortDistance_Escort: [trip] }} />,
        Download: <span style={{ color: "#6b7280" }}>Not Saved</span>,
        EndRide: trip?.destination || trip?.finalDestination || "N/A",
        currentTimeStamp: new Date(trip?.createdAt || trip?.currentTimeStamp || trip?.startTime || 0).getTime(),
      };
    });
  };

  const loadCargoEscort = (historyData) => {
    const { user = {}, cargo } = historyData;
    const normalized = normalizeCollection(cargo);
    const trips = sortByTimestampDesc(normalized);

    return trips.map((trip, index) => {
      const regNo =
        trip?.VhiclePlate ||
        trip?.vehicleNumber ||
        trip?.vehicleReg ||
        trip?.regNo ||
        "N/A";

      const vehicleImg =
        trip?.DriverImage ||
        trip?.vehicleImage ||
        trip?.imgVehicle ||
        user?.userImage;

      const dateDisplay =
        trip?.departureDate ||
        trip?.ArrivalDate ||
        formatTime(trip?.createdAt || trip?.currentTimeStamp);

      const timeDisplay =
        trip?.TripTime ||
        formatTime(trip?.startTime || trip?.createdAt || trip?.currentTimeStamp);

      const endDisplay = trip?.deliveryAttestation?.deliveredAt
        ? formatTime(trip?.deliveryAttestation?.deliveredAt)
        : formatTime(trip?.endTime || trip?.tripendtime);

      return {
        Sr: index + 1,
        Name: trip?.RequesterName || user?.userName || "N/A",
        Email: trip?.RequesterEmail || user?.userEmail || "N/A",
        Mobile: trip?.RequesterPhoneNo || user?.userPhone || "N/A",
        VehicleReg: regNo,
        State: trip?.state || user?.state || "N/A",
        VehicleImage: (
          <img
            onClick={() => handleImageClick(vehicleImg)}
            src={vehicleImg}
            alt="vehicle"
            style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
          />
        ),
        TripDate: dateDisplay || "N/A",
        TripTime: timeDisplay || "N/A",
        TripEndTime: endDisplay || "N/A",
        IsMoving: trip?.status || trip?.Trip_Status || "N/A",
        TripType: <h5>Cargo Escort ({trip?.cargoType || "Cargo"})</h5>,
        StartRide:
          trip?.pickupLocation ||
          trip?.startingLocation ||
          trip?.currentLocation ||
          "N/A",
        Notification: "N/A",
        File: <File props={trip} />,
        Chat: <ShowChatData props={trip} />,
        Report: <EscortReport props={{ ...user, ShortDistance_Escort: [trip] }} />,
        Download: <span style={{ color: "#6b7280" }}>Not Saved</span>,
        EndRide: trip?.deliveryLocation || trip?.destination || trip?.finalDestination || "N/A",
        currentTimeStamp: new Date(trip?.createdAt || trip?.currentTimeStamp || trip?.startTime || 0).getTime(),
      };
    });
  };

  const loadDriverEscort = (historyData) => {
    const { user = {}, driverEscort } = historyData;
    let normalized = normalizeCollection(driverEscort);
    if (normalized.length === 0 && historyData?.shortDistance) {
      const shortList = normalizeCollection(historyData.shortDistance);
      normalized = shortList.filter(
        (t) => t?.isDriverEscort || t?.driverEscort || t?.source === "EscortTrip" || user?.isDriver
      );
    }
    const trips = sortByTimestampDesc(normalized);

    return trips.map((trip, index) => {
      const regNo =
        trip?.vehicleReg ||
        trip?.regNo ||
        trip?.driverId?.regNo ||
        trip?.driverId?.vehicleReg ||
        trip?.driverId?.vehiclePlate ||
        user?.driver?.regNo ||
        "N/A";

      const vehicleImg =
        trip?.driverId?.imgVehicle ||
        trip?.driverId?.vehicleImage ||
        trip?.driverId?.driverImage ||
        trip?.imgVehicle ||
        trip?.vehicleImage ||
        user?.driver?.imgVehicle ||
        user?.userImage;

      const dateDisplay =
        trip?.ArrivalDate ||
        formatTime(trip?.createdAt || trip?.currentTimeStamp || trip?.startTime);

      const timeDisplay =
        trip?.TripTime ||
        trip?.ArrivalTime ||
        formatTime(trip?.startTime || trip?.createdAt || trip?.currentTimeStamp);

      const endDisplay = formatTime(trip?.endTime || trip?.tripendtime);

      const driverName =
        trip?.driverName ||
        (trip?.driverId?.driverName &&
          `${trip?.driverId?.driverName} ${trip?.driverId?.surName || ""}`.trim()) ||
        trip?.driverId?.name ||
        user?.userName ||
        "N/A";

      const driverEmail =
        trip?.driverEmail ||
        trip?.driverId?.email ||
        user?.userEmail ||
        "N/A";

      const driverMobile =
        trip?.driverPhone ||
        trip?.driverId?.phoneNumber ||
        user?.userPhone ||
        "N/A";

      return {
        Sr: index + 1,
        Name: driverName,
        Email: driverEmail,
        Mobile: driverMobile,
        VehicleReg: regNo,
        State: trip?.state || trip?.driverId?.state || user?.state || "N/A",
        VehicleImage: (
          <img
            onClick={() => handleImageClick(vehicleImg)}
            src={vehicleImg}
            alt="vehicle"
            style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
          />
        ),
        TripDate: dateDisplay || "N/A",
        TripTime: timeDisplay || "N/A",
        TripEndTime: endDisplay || "N/A",
        IsMoving: trip?.status || trip?.Trip_Status || (trip?.escortInProgress ? "ACTIVE" : "COMPLETED"),
        TripType: <h5>Driver Escort</h5>,
        StartRide:
          trip?.startingLocation ||
          trip?.currentLocation ||
          trip?.currentLocTxt ||
          trip?.pickupLocation ||
          "N/A",
        Notification:
          trip?.contactNotificationStatus && trip?.contactNotificationStatus?.length > 0 ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleOpenNotification(trip.contactNotificationStatus)}
            >
              Show Notification
            </button>
          ) : (
            "No Notification"
          ),
        File: <File props={trip} />,
        Chat: <ShowChatData props={trip} />,
        Report: <EscortReport props={{ ...user, ShortDistance_Escort: [trip] }} />,
        Download: <span style={{ color: "#6b7280" }}>Not Saved</span>,
        EndRide:
          trip?.destination || trip?.finalDestination ? (
            <span style={{ fontWeight: 600 }}>{trip?.destination || trip?.finalDestination}</span>
          ) : ["COMPLETED", "ESCALATED", "CANCELED"].includes(trip?.status) ? (
            <span style={{ color: "#b91c1c", fontWeight: 600 }}>{trip?.status}</span>
          ) : (
            <span style={{ color: "#15803d", fontWeight: 600 }}>Active</span>
          ),
        currentTimeStamp: new Date(
          trip?.createdAt || trip?.currentTimeStamp || trip?.startTime || 0
        ).getTime(),
      };
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    const historyData = await fetchHistoryData();
    if (!historyData) {
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      let tableData = [];

      if (activeTab === "short") tableData = loadShortDistance(historyData);
      if (activeTab === "long") tableData = loadLongDistance(historyData);
      if (activeTab === "cargo") tableData = loadCargoEscort(historyData);
      if (activeTab === "driver") tableData = loadDriverEscort(historyData);

      const sortedRows = sortByTimestampDesc(tableData);
      const finalRows = sortedRows.map((row, index) => ({ ...row, Sr: index + 1 }));
      setRows(finalRows);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error(error.message || "Failed to process data");
    } finally {
      setLoading(false);
    }
  }, [activeTab, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = rows.filter((row) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    const isMovingText =
      typeof row?.IsMoving === "string"
        ? row?.IsMoving
        : typeof row?.IsMoving?.props?.children === "string"
        ? row?.IsMoving?.props?.children
        : "";
    const endRideText =
      typeof row?.EndRide === "string"
        ? row?.EndRide
        : typeof row?.EndRide?.props?.children === "string"
        ? row?.EndRide?.props?.children
        : "";

    return (
      (row?.Name || "").toLowerCase().includes(s) ||
      (row?.Email || "").toLowerCase().includes(s) ||
      (row?.Mobile || "").toLowerCase().includes(s) ||
      (row?.VehicleReg || "").toLowerCase().includes(s) ||
      (row?.State || "").toLowerCase().includes(s) ||
      (row?.TripDate || "").toLowerCase().includes(s) ||
      (row?.TripTime || "").toLowerCase().includes(s) ||
      (row?.StartRide || "").toLowerCase().includes(s) ||
      endRideText.toLowerCase().includes(s) ||
      isMovingText.toLowerCase().includes(s)
    );
  });

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <ToastContainer />

        <Box display="flex" justifyContent="center" gap={2} mb={4} flexWrap="wrap">
          <button
            className="btn btn-primary btn pe-3 ps-3"
            style={{
              border: activeTab === "short" ? "3px solid #1976d2" : "1px solid #ccc",
              backgroundColor: activeTab === "short" ? "#1976d2" : "white",
              color: activeTab === "short" ? "white" : "black",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => setActiveTab("short")}
          >
            Short Distance Escort
          </button>

          <button
            className="btn btn-primary btn pe-3 ps-3"
            style={{
              border: activeTab === "long" ? "3px solid #1976d2" : "1px solid #ccc",
              backgroundColor: activeTab === "long" ? "#1976d2" : "white",
              color: activeTab === "long" ? "white" : "black",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => setActiveTab("long")}
          >
            Long Distance Escort
          </button>

          <button
            className="btn btn-primary btn pe-3 ps-3"
            style={{
              border: activeTab === "cargo" ? "3px solid #1976d2" : "1px solid #ccc",
              backgroundColor: activeTab === "cargo" ? "#1976d2" : "white",
              color: activeTab === "cargo" ? "white" : "black",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => setActiveTab("cargo")}
          >
            Cargo Escort
          </button>

          <button
            className="btn btn-primary btn pe-3 ps-3"
            style={{
              border: activeTab === "driver" ? "3px solid #1976d2" : "1px solid #ccc",
              backgroundColor: activeTab === "driver" ? "#1976d2" : "white",
              color: activeTab === "driver" ? "white" : "black",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => setActiveTab("driver")}
          >
            Driver Escort
          </button>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {activeTab === "short" && "Short Distance Escort History"}
                  {activeTab === "long" && "Long Distance Escort History"}
                  {activeTab === "cargo" && "Cargo Escort History"}
                  {activeTab === "driver" && "Driver Escort History"}
                </MDTypography>
                <MDTypography variant="button" color="white" fontWeight="medium">
                  Total: {rows.length} {rows.length === 1 ? "Record" : "Records"}
                </MDTypography>
              </MDBox>

              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
                px={3}
                pt={3}
                pb={1}
              >
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  {searchTerm.trim()
                    ? `Showing ${filteredRows.length} of ${rows.length} records matching "${searchTerm}"`
                    : `Total Records: ${rows.length}`}
                </MDTypography>
                <MDBox width={{ xs: "100%", sm: "18rem" }}>
                  <TextField
                    size="small"
                    placeholder="Search by name, reg, state, etc..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                  />
                </MDBox>
              </MDBox>

              <MDBox pt={2}>
                {loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px 0",
                      fontSize: "18px",
                    }}
                  >
                    Loading...
                  </div>
                ) : filteredRows.length > 0 ? (
                  <DataTable
                    table={{ columns, rows: filteredRows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 20, 50, 100] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                ) : (
                  <EmptyData />
                )}
              </MDBox>
            </Card>

            <ImageModal
              onClickImageData={onClickImageData}
              onClose={closeModal}
              isModalOpen={isModalOpen}
            />
            <NotificationModal
              open={openNotification}
              handleClose={handleCloseNotification}
              notifications={selectedNotifications}
            />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserEscortHistory;
